import axios from 'axios'
import { environmentConfig } from '@/config/enviromentConfig'
import { premiumEnvironment } from '@/brand'

// Telkom Premium API, called from the subscriber's browser (never server-side: header enrichment
// only exists on requests the handset makes over Telkom mobile data). No credentials; Telkom
// allows our page origins via CORS.
export const PremiumErrors = {
  INVALID_MSISDN: 'INVALID_MSISDN',
  INVALID_OTP: 'INVALID_OTP',
  OPTIN_ALREADY_ACTIVE: 'OPTIN_ALREADY_ACTIVE',
  ANTI_FRAUD_BLOCK: 'ANTI_FRAUD_BLOCK',
  PARTNER_IS_NOT_PREMIUM: 'PARTNER_IS_NOT_PREMIUM',
  PRODUCT_IS_NOT_ONLINE: 'PRODUCT_IS_NOT_ONLINE',
  NETWORK: 'NETWORK',
  NO_SERVICE: 'NO_SERVICE'
}

const http = axios.create({ timeout: 25000, validateStatus: () => true })

function mode() {
  const requested = new URLSearchParams(window.location.search).get('mode')
  return requested === 'mock' || requested === 'live' ? requested : environmentConfig.telkomPremiumMode
}

// Production by default; ?env=staging, or a staging host (src/brand/index.js), points a page load at
// the staging host.
function env() {
  return premiumEnvironment()
}

function baseUrl() {
  const url = env() === 'staging' ? environmentConfig.telkomPremiumStagingBaseUrl : environmentConfig.telkomPremiumBaseUrl
  return String(url || '').replace(/\/$/, '')
}

function normalise(response) {
  const data = response?.data && typeof response.data === 'object' ? response.data : {}
  const errorCode = data.errorCode || (response.status >= 400 ? `HTTP_${response.status}` : '')
  return {
    ok: !errorCode,
    status: response.status,
    transactionId: data.transactionId ?? null,
    subscriptionId: data.subscriptionId ?? null,
    errorCode,
    error: data.error || ''
  }
}

// GET /premium/he -> "true" | "false" | "x=<token>". The HE server answer may come back as the
// body or on the final URL after the redirect axios follows for us.
function parseHeaderEnrichment(response) {
  const body = typeof response.data === 'string' ? response.data.trim() : String(response.data ?? '').trim()
  const finalUrl = response.request?.responseURL || ''
  let token = null
  const match = body.match(/^x=(.+)$/i)
  if (match) token = match[1].trim()
  if (!token && finalUrl) {
    try {
      token = new URL(finalUrl).searchParams.get('x')
    } catch {
      token = null
    }
  }
  if (token) return { identified: true, token }
  if (/^true$/i.test(body)) return { identified: true, token: null }
  return { identified: false, token: null }
}

// Everything the /he call returned goes to the console and to the pod (POST /he-log -> HE_PROBE
// line in kubectl logs), so a test on a handset can be read back without the handset.
export function reportHeaderEnrichment(result) {
  const payload = { kind: 'premium-he', page: window.location.href, ua: navigator.userAgent, time: new Date().toISOString(), ...result }
  console.info('[HE]', payload)
  try {
    fetch('/he-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit',
      keepalive: true
    }).catch(() => {})
  } catch {
    // nothing to do without a pod behind us (local dev server)
  }
}

// The /premium/he call public/index.html started before the app booted, taken once.
async function takeEarlyHeaderEnrichment() {
  const early = window.__WR_HE_EARLY__
  delete window.__WR_HE_EARLY__
  if (!early?.response || early.url !== `${baseUrl()}/premium/he`) return null
  const answer = await early.response
  if (answer.error) {
    return { identified: false, token: null, status: 0, url: early.url, error: answer.error, code: 'ERR_NETWORK', ms: answer.ms, early: true }
  }
  const parsed = answer.status >= 400 ? { identified: false, token: null } : parseHeaderEnrichment({ data: answer.body, request: { responseURL: answer.finalUrl } })
  return { ...parsed, status: answer.status, url: early.url, finalUrl: answer.finalUrl, body: String(answer.body ?? '').slice(0, 500), ms: answer.ms, early: true }
}

class LivePremium {
  static async checkHeaderEnrichment() {
    const early = await takeEarlyHeaderEnrichment()
    if (early) {
      reportHeaderEnrichment(early)
      return early
    }
    const url = `${baseUrl()}/premium/he`
    const started = Date.now()
    let response
    try {
      response = await http.get(url, { responseType: 'text' })
    } catch (error) {
      // CORS refusal, mixed content or no network: not identified, but keep the reason for ?debug.
      const failed = { identified: false, token: null, status: 0, url, error: error?.message || String(error), code: error?.code, ms: Date.now() - started }
      reportHeaderEnrichment(failed)
      return failed
    }
    const parsed = response.status >= 400 ? { identified: false, token: null } : parseHeaderEnrichment(response)
    const result = {
      ...parsed,
      status: response.status,
      url,
      finalUrl: response.request?.responseURL || '',
      body: String(response.data ?? '').slice(0, 500),
      headers: response.headers,
      ms: Date.now() - started
    }
    reportHeaderEnrichment(result)
    return result
  }

  static async requestOtp(svcId, extRef, msisdn) {
    const response = await http.post(
      `${baseUrl()}/premium/${encodeURIComponent(svcId)}/otp`,
      { msisdnOtp: msisdn },
      { params: { ext_ref: extRef }, headers: { 'Content-Type': 'application/json' } }
    )
    return normalise(response)
  }

  static async subscribe(svcId, extRef, body, token) {
    const params = { ext_ref: extRef }
    if (token) params.x = token
    const response = await http.post(
      `${baseUrl()}/premium/${encodeURIComponent(svcId)}/sub`,
      body,
      { params, headers: { 'Content-Type': 'application/json' } }
    )
    return normalise(response)
  }
}

// Mock for working without Telkom: ?he=true|token|false picks the HE outcome, the OTP is 1234,
// ?fail=<errorCode> forces that error on /sub.
const MOCK_OTP = '1234'
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const query = (key) => new URLSearchParams(window.location.search).get(key)

class MockPremium {
  static async checkHeaderEnrichment() {
    await wait(900)
    const he = query('he')
    if (he === 'true') return { identified: true, token: null, status: 200, mock: true }
    if (he === 'token') return { identified: true, token: 'mock-he-token', status: 200, mock: true }
    return { identified: false, token: null, status: 200, mock: true }
  }

  static async requestOtp(svcId, extRef, msisdn) {
    await wait(700)
    if (!/^27\d{9}$/.test(msisdn)) return { ok: false, status: 200, transactionId: null, subscriptionId: null, errorCode: PremiumErrors.INVALID_MSISDN, error: 'Invalid MSISDN' }
    return { ok: true, status: 200, transactionId: `mock-tx-${extRef}`, subscriptionId: null, errorCode: '', error: '' }
  }

  static async subscribe(svcId, extRef, body) {
    await wait(1100)
    const forced = query('fail')
    if (forced) return { ok: false, status: 200, transactionId: `mock-tx-${extRef}`, subscriptionId: null, errorCode: forced, error: forced }
    if (body.otp !== undefined && body.otp !== MOCK_OTP) return { ok: false, status: 200, transactionId: `mock-tx-${extRef}`, subscriptionId: null, errorCode: PremiumErrors.INVALID_OTP, error: 'Invalid OTP' }
    return { ok: true, status: 200, transactionId: `mock-tx-${extRef}`, subscriptionId: 2140000000 + Math.floor(Math.random() * 999999), errorCode: '', error: '' }
  }
}

export function isMockMode() {
  return mode() === 'mock'
}

export function premiumMode() {
  return mode()
}

export function premiumBaseUrl() {
  return baseUrl()
}

export function premiumEnv() {
  return env()
}

export default function TelkomPremiumService() {
  return isMockMode() ? MockPremium : LivePremium
}
